<template>
    <div class="auth-wrapper" ref="authRoot">
      <div class="login-container" :style="{ transform: `scale(${computedScale})` }">
        <authenticator 
        :hideSignUp="true"
        :formFields="formFields"
        class="amplify-auth"
        >
        <template v-slot:header>
          <div class="login-header">
            <div class="login-logo">
              <div class="logo">
                <img :src="logoUrl" class="login-logo-img"/>
              </div>
              <div class="brand-text">
                <p>Developer Portal</p>
              </div>
            </div>
          </div>
        </template>      
        <template v-slot:sign-in-header>
          <div class="login-card-header">
            <h2 style="padding-top: 3rem;">Bem-vindo(a)</h2>
            <p>Faça login para acessar o catálogo</p>
          </div>
        </template> 
        <template v-slot="{ user }">
          <!-- Redirecionar automaticamente para dashboard após login bem-sucedido -->
          <div v-if="user">
            {{ handleUserAuthenticated(user) }}
          </div>
        </template>
        </authenticator>
      </div>
    </div>
  </template>
  
  <script setup lang="ts">
    import { Authenticator } from "@aws-amplify/ui-vue";
    import "@aws-amplify/ui-vue/styles.css";
    import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue';
    import { useRouter } from 'vue-router';
    import { useAuthStore } from '../stores/auth';
    import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
    const logoUrl = '/faviconLwsa.ico'

    // Interface para o usuário do Amplify
    interface AmplifyUser {
      username?: string;
      signInDetails?: {
        loginId?: string;
      };
      attributes?: {
        name?: string;
        email?: string;
        picture?: string;
      };
    }

    // Propriedade para controlar a escala da tela de login
    const props = withDefaults(defineProps<{
      scale?: number;
    }>(), {
      scale: 0.75
    });

    // Escala computada com limites de segurança
    const computedScale = computed(() => {
      const rawScale = props.scale;
      // Limita entre 0.5 (50%) e 1.5 (150%) para evitar extremos
      return Math.min(1.5, Math.max(0.5, Number.isFinite(rawScale) ? rawScale : 1));
    });

    const router = useRouter();
    const authStore = useAuthStore();
    const isRedirecting = ref(false);
    const skipAutoLogin = ref(false);
    const authRoot = ref<HTMLElement | null>(null);
    let passwordObserver: MutationObserver | null = null;

    // Placeholders personalizados para os campos do Amplify
    const formFields = {
      signIn: {
        username: {
          placeholder: 'seu.email@lwsa.tech'
        },
        password: {
          placeholder: '••••••••'
        }
      }
    } as const;

    // Função para adicionar botão de toggle ao campo de senha existente
    const addPasswordToggle = () => {
      if (!authRoot.value) return;
      
      const rootEl = authRoot.value as HTMLElement;
      const passwordInput = rootEl.querySelector('input[type="password"]') as HTMLInputElement | null;
      
      if (!passwordInput) return;
      
      // Verificar se já tem botão
      if (passwordInput.parentElement?.querySelector('.password-toggle')) return;
      
      // Criar wrapper se não existir
      let wrapper = passwordInput.parentElement;
      if (!wrapper || !wrapper.classList.contains('password-input-container')) {
        wrapper = document.createElement('div');
        wrapper.className = 'password-input-container';
        wrapper.style.position = 'relative';
        passwordInput.parentNode?.insertBefore(wrapper, passwordInput);
        wrapper.appendChild(passwordInput);
      }
      
      // Ajustar padding do input
      passwordInput.style.paddingRight = '3rem';
      
      // Criar botão de toggle
      const toggleButton = document.createElement('button');
      toggleButton.type = 'button';
      toggleButton.className = 'password-toggle';
      toggleButton.setAttribute('aria-label', 'Alternar visibilidade da senha');
      toggleButton.setAttribute('aria-pressed', 'false');
      toggleButton.innerHTML = `
        <svg fill="currentColor" viewBox="0 0 24 24" class="password-icon">
          <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z"/>
        </svg>
      `;
      
      // Adicionar evento de click
      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isPassword = passwordInput.type === 'password';
        passwordInput.type = isPassword ? 'text' : 'password';
        toggleButton.setAttribute('aria-pressed', isPassword ? 'true' : 'false');
        
        // Atualizar ícone
        toggleButton.innerHTML = isPassword ? `
          <svg fill="currentColor" viewBox="0 0 24 24" class="password-icon">
            <path d="M11.83,9L15,12.16C15,12.11 15,12.05 15,12A3,3 0 0,0 12,9C11.94,9 11.89,9 11.83,9M7.53,9.8L9.08,11.35C9.03,11.56 9,11.77 9,12A3,3 0 0,0 12,15C12.22,15 12.44,14.97 12.65,14.92L14.2,16.47C13.53,16.8 12.79,17 12,17A5,5 0 0,1 7,12C7,11.21 7.2,10.47 7.53,9.8M2,4.27L4.28,6.55L4.73,7C3.08,8.3 1.78,10 1,12C2.73,16.39 7,19.5 12,19.5C13.55,19.5 15.03,19.2 16.38,18.66L16.81,19.09L19.73,22L21,20.73L3.27,3M12,7A5,5 0 0,1 17,12C17,12.64 16.87,13.26 16.64,13.82L19.57,16.75C21.07,15.5 22.27,13.86 23,12C21.27,7.61 17,4.5 12,4.5C10.6,4.5 9.26,4.75 8,5.2L10.17,7.35C10.76,7.13 11.37,7 12,7Z"/>
          </svg>
        ` : `
          <svg fill="currentColor" viewBox="0 0 24 24" class="password-icon">
            <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z"/>
          </svg>
        `;
      });
      
      // Adicionar o botão ao wrapper
      wrapper.appendChild(toggleButton);
    };

    // Verificar se acabamos de vir de um logout
    onMounted(() => {
      // Se o authStore não está autenticado mas estamos na página de login,
      // significa que provavelmente viemos de um logout
      if (!authStore.isAuthenticated) {
        skipAutoLogin.value = true;
        // Limpar o flag após um tempo para permitir login normal
        setTimeout(() => {
          skipAutoLogin.value = false;
        }, 1000);
      }
      nextTick(() => {
        if (authRoot.value) {
          passwordObserver = new MutationObserver(() => addPasswordToggle());
          passwordObserver.observe(authRoot.value, { childList: true, subtree: true });
          addPasswordToggle();
        }
      });
    });

    onUnmounted(() => {
      if (passwordObserver) {
        passwordObserver.disconnect();
        passwordObserver = null;
      }
    });

    // Função para lidar com usuário autenticado no template
    const handleUserAuthenticated = (user: AmplifyUser | null) => {
      // Não fazer login automático se acabamos de fazer logout
      if (user && !isRedirecting.value && !skipAutoLogin.value) {
        authenticateAndRedirect(user);
      }
      return ''; // Retorna string vazia para não mostrar nada no template
    };

    // Função para autenticar usando o authStore e redirecionar
    const authenticateAndRedirect = async (amplifyUser: AmplifyUser) => {
      if (isRedirecting.value || skipAutoLogin.value) return;
      
      isRedirecting.value = true;
      console.log('Iniciando processo de autenticação com Amplify e authStore...');
      
      try {
        // Buscar grupos e tentar obter o nome pelo ID Token (claim padrão "name")
        let roles: string[] = [];
        let nameFromToken: string | undefined;
        try {
          const { tokens } = await fetchAuthSession();
          const groupsClaim = tokens?.idToken?.payload?.["cognito:groups"] as unknown;
          if (Array.isArray(groupsClaim)) {
            roles = groupsClaim as string[];
          } else if (typeof groupsClaim === 'string') {
            roles = [groupsClaim];
          }
          const tokenName = tokens?.idToken?.payload?.name;
          if (typeof tokenName === 'string' && tokenName.trim()) {
            nameFromToken = tokenName.trim();
          }
        } catch (e) {
          console.warn('Não foi possível obter grupos/nome do Cognito via sessão:', e);
        }

        // Tentar obter o nome diretamente dos atributos do usuário (API) se ainda não houver
        let nameFromAttributes: string | undefined = amplifyUser.attributes?.name?.trim();
        if (!nameFromAttributes) {
          try {
            const attrs = await fetchUserAttributes();
            if (attrs?.name && attrs.name.trim()) {
              nameFromAttributes = attrs.name.trim();
            }
          } catch (e) {
            console.warn('Não foi possível obter atributos do usuário (name):', e);
          }
        }

        const resolvedName = nameFromAttributes
          || nameFromToken
          || amplifyUser.signInDetails?.loginId
          || amplifyUser.username
          || '';

        // Extrair dados do usuário do Amplify
        const userData = {
        name: resolvedName,
        email: amplifyUser.signInDetails?.loginId || amplifyUser.attributes?.email || 'user@amplify.com',
        roles, // já vem dos grupos via cognito:groups
        avatar: amplifyUser.attributes?.picture || null,
        amplifyUser
}
        
        // Usar o authStore para fazer login com os dados do Amplify
        await authStore.loginWithAmplify(userData, amplifyUser);
        
        console.log('Autenticação realizada via authStore, redirecionando...');
        
        // Redirecionar usando o router
        await router.push('/dashboard');
        
      } catch (error) {
        console.error('Erro na autenticação:', error);
        isRedirecting.value = false;
      }
    };

  </script>
  
  <style scoped>
  .auth-wrapper {
    height: 100vh; /* altura fixa para evitar scroll */
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    /* Padding base sem duplicação */
    padding: clamp(8px, 2vh, 20px) clamp(8px, 2vw, 20px);
    /* Áreas seguras apenas quando necessárias */
    padding-top: max(env(safe-area-inset-top), clamp(8px, 2vh, 20px));
    padding-bottom: max(env(safe-area-inset-bottom), clamp(8px, 2vh, 20px));
    padding-left: max(env(safe-area-inset-left), clamp(8px, 2vw, 20px));
    padding-right: max(env(safe-area-inset-right), clamp(8px, 2vw, 20px));
    overflow: hidden; /* remove scroll completamente */
    box-sizing: border-box;
    background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
    /* Neutraliza estilos globais que aplicavam escala */
    transform: none !important;
    transform-origin: center center !important;
  }

  .login-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transform-origin: center center;
    transition: transform 0.3s ease;
    will-change: transform;
  }

  /* Prioriza 100dvh (altura visível dinâmica), depois 100svh */
  @supports (height: 100dvh) {
    .auth-wrapper { height: 100dvh; }
  }
  @supports (height: 100svh) {
    .auth-wrapper { height: 100svh; }
  }

  /* Em telas com altura muito baixa, permitir scroll para não cortar conteúdo */
  /* Removido para evitar barra de rolagem vertical local */

  .login-header {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: clamp(8px, 2vh, 24px);
  }

  .login-logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: clamp(4px, 1vh, 16px);
  }

  .login-logo-img {
    width: clamp(72px, 14vw, 128px);
    height: auto;
  }

  .brand-text p {
    font-size: clamp(0.875rem, 2.8vw, 1.25rem);
    font-weight: 600;
    color: #374151;
    text-align: center;
    margin: 0;
  }

  :deep([data-amplify-router]) {
    width: min(92vw, 420px) !important;
    margin: 0 !important;
    padding: clamp(16px, 3vh, 28px) clamp(16px, 4vw, 32px) !important;
    border-radius: 12px !important;
    box-shadow: none !important;
    background: #ffffff !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    display: block !important;
  }

  .login-card-header h2 {
    font-size: clamp(1.125rem, 3.5vw, 1.5rem) !important;
    padding-top: clamp(8px, 2vh, 20px) !important;
    margin-bottom: clamp(4px, 1.5vh, 12px) !important;
    text-align: center;
    color: #111827;
  }

  .login-card-header p {
    font-size: clamp(0.8rem, 2.8vw, 0.95rem) !important;
    text-align: center;
    color: #6b7280;
    margin-bottom: clamp(8px, 2vh, 16px);
  }

  :deep(.amplify-input) {
    font-size: clamp(0.8rem, 2.8vw, 0.95rem) !important;
    padding: clamp(10px, 2.2vh, 14px) clamp(12px, 3.2vw, 18px) !important;
    border-radius: 8px !important;
    border: 2px solid #e5e7eb !important;
  }

  :deep(.amplify-label) {
    font-size: clamp(0.7rem, 2.2vw, 0.825rem) !important;
    font-weight: 600 !important;
    color: #374151 !important;
    margin-bottom: 4px !important;
  }

  :deep(.amplify-button--primary) {
    width: 100% !important;
    padding: clamp(10px, 2.2vh, 14px) clamp(16px, 4vw, 20px) !important;
    font-size: clamp(0.8rem, 2.8vw, 0.95rem) !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    background: linear-gradient(135deg, #111827 0%, #374151 100%) !important;
    border: none !important;
    margin-top: clamp(10px, 2vh, 16px) !important;
  }

  :deep(.password-toggle) {
    width: clamp(24px, 4vw, 32px) !important;
    height: clamp(24px, 4vw, 32px) !important;
    right: clamp(8px, 2vw, 16px) !important;
  }

  :deep(.password-icon) {
    width: clamp(16px, 2.5vw, 20px) !important;
    height: clamp(16px, 2.5vw, 20px) !important;
  }

  @media (max-height: 600px) {
    .auth-wrapper {
      padding: 6px 8px;
    }
    :deep([data-amplify-router]) {
      padding: 12px 14px !important;
      width: min(96vw, 360px) !important;
      max-height: calc(100svh - 72px) !important;
    }
    .login-card-header h2 { padding-top: 6px !important; }
  }
  </style>